import { PopoverClose } from '@radix-ui/react-popover';
import { isEmpty } from 'lodash';
import React from 'react';
import { LuEllipsisVertical, LuSquarePen, LuTrash } from 'react-icons/lu';

import { RestrictionType } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { TickerLogo } from '@/components/TickerLogo';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableSkeletonRow } from '@/components/ui/table-skeleton-row';

import { restrictionTypeMap } from '../constants';

interface RestrictionsTableProps {
    restrictions: Array<{
        shareId: string;
        shareClassId?: string;
        restrictionType: RestrictionType;
    }>;
    assetsMap: Map<string, AssetInfo>;
    isLoading: boolean;
    onEdit: (shareId: string, shareClassId?: string) => void;
    onDelete: (shareId: string, shareClassId?: string) => void;
}

export const RestrictionsTable: React.FC<RestrictionsTableProps> = ({
    restrictions,
    assetsMap,
    isLoading,
    onEdit,
    onDelete,
}) => {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="font-medium text-gray-700">Company</TableHead>
                    <TableHead className="font-medium text-gray-700">Name</TableHead>
                    <TableHead className="font-medium text-gray-700">Restrictions</TableHead>
                    <TableHead className="w-20"></TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {!isLoading && isEmpty(restrictions) && (
                    <TableRow>
                        <TableCell colSpan={4} className="h-24 text-center">
                            No restrictions found
                        </TableCell>
                    </TableRow>
                )}
                {isLoading
                    ? [1, 2, 3].map((i) => <TableSkeletonRow key={`loading-row-${i}`} columns={3} />)
                    : restrictions.map((item) => {
                          return (
                              <TableRow key={item.shareId + (item.shareClassId || '')}>
                                  <TableCell>
                                      <div className="flex items-center gap-2">
                                          <TickerLogo
                                              tickerSymbol={assetsMap.get(item.shareId)?.symbol}
                                              companyName={assetsMap.get(item.shareId)?.description}
                                              className="h-8 w-8 rounded object-cover"
                                              fallbackIconClassName="w-8 h-8"
                                          />
                                          <span>{assetsMap.get(item.shareId)?.symbol}</span>
                                      </div>
                                  </TableCell>
                                  <TableCell>{assetsMap.get(item.shareId)?.description}</TableCell>
                                  <TableCell>{restrictionTypeMap[item.restrictionType]}</TableCell>
                                  <TableCell>
                                      <Popover>
                                          <PopoverTrigger asChild>
                                              <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className="h-8 w-8 p-0 hover:bg-gray-100"
                                              >
                                                  <LuEllipsisVertical className="h-4 w-4 text-black" />
                                              </Button>
                                          </PopoverTrigger>
                                          <PopoverContent className="w-32 p-0">
                                              <div className="py-1">
                                                  <PopoverClose asChild>
                                                      <button
                                                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                          onClick={() => onEdit(item.shareId, item.shareClassId)}
                                                      >
                                                          <LuSquarePen className="h-4 w-4" />
                                                          Edit
                                                      </button>
                                                  </PopoverClose>
                                                  <PopoverClose asChild>
                                                      <button
                                                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100"
                                                          onClick={() => onDelete(item.shareId, item.shareClassId)}
                                                      >
                                                          <LuTrash className="h-4 w-4" />
                                                          Remove
                                                      </button>
                                                  </PopoverClose>
                                              </div>
                                          </PopoverContent>
                                      </Popover>
                                  </TableCell>
                              </TableRow>
                          );
                      })}
            </TableBody>
        </Table>
    );
};
